import { useMemo, useState } from "react";

import { Dialog, Paragraph, Select, type SelectItem } from "@fronterahealth/frontera-ui-components";

import {
  type ExpertChatOversightRow,
  type ExpertChatProvider,
  useInternalReassignExpertChatCreate,
} from "@api/rest/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

interface ReassignChatDialogProps {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  chat: ExpertChatOversightRow | null;
  providers: ExpertChatProvider[];
  isLoadingProviders?: boolean;
  onReassigned: () => void;
}

export const ReassignChatDialog: React.FC<ReassignChatDialogProps> = ({
  isOpen,
  setOpen,
  chat,
  providers,
  isLoadingProviders,
  onReassigned,
}) => {
  const [selected, setSelected] = useState<SelectItem | undefined>(undefined);

  // Clear the picker on every close path — the Dialog routes its own X,
  // backdrop and escape handling through setOpen — so reopening on a
  // different chat can't inherit a stale selection.
  const handleSetOpen = (open: boolean) => {
    if (!open) setSelected(undefined);
    setOpen(open);
  };

  const items = useMemo<SelectItem[]>(
    () =>
      providers
        .filter((provider) => !provider.archived)
        // Reassigning to the provider already on the chat returns a 409, so
        // don't offer it.
        .filter((provider) => provider.provider_id !== chat?.assigned_provider_id)
        .map((provider) => ({
          id: String(provider.provider_id),
          primary: provider.name,
          secondary: provider.email ?? undefined,
        })),
    [providers, chat?.assigned_provider_id],
  );

  const close = () => handleSetOpen(false);

  const reassign = useInternalReassignExpertChatCreate({
    mutation: {
      onSuccess: (response) => {
        // customFetch resolves for every status code, so branch on it here
        // rather than relying on onError. 409 means the chat moved or closed
        // under us; 400 means a bad learner or provider id.
        if (response.status !== 200) {
          notifyError(
            ("detail" in response.data && response.data.detail) || "Couldn't reassign this chat. Please try again.",
          );
          return;
        }
        notifySuccess("Chat reassigned.");
        close();
        onReassigned();
      },
      onError: () => notifyError("Couldn't reassign this chat. Please try again."),
    },
  });

  const submit = () => {
    if (!chat?.learner_id || !selected?.id) return;
    reassign.mutate({
      data: { learner_id: chat.learner_id, provider_id: Number(selected.id) },
    });
  };

  return (
    <Dialog
      title="Reassign chat"
      open={isOpen}
      setOpen={handleSetOpen}
      size="lg"
      primaryButton={{
        text: reassign.isPending ? "Reassigning..." : "Reassign",
        onClick: submit,
        disabled: !selected || reassign.isPending,
      }}
      secondaryButton={{ text: "Cancel", onClick: close }}
    >
      <div className="flex flex-col gap-4">
        <Paragraph size="small" colorType="secondary">
          The current chat is closed and the new expert starts a fresh one with{" "}
          {chat?.caregiver_name || "the caregiver"}.
        </Paragraph>
        <Select
          title="New expert"
          placeholderText={isLoadingProviders ? "Loading experts..." : "Select an expert"}
          items={items}
          selected={selected}
          setSelected={setSelected}
          enableSearch
          isLoading={isLoadingProviders}
        />
      </div>
    </Dialog>
  );
};
